// ============================================
// Patient Auth Form
//
// Email + password form for patient login/register.
// Design system: light theme, Instrument Serif titles,
// DM Sans body, Rose #E11D48 accent, 4px border-radius,
// 13px min font, WCAG AA contrast.
// No framer-motion. Pure CSS transitions.
// ============================================

import { useState } from 'react';
import { Mail, Lock, Loader2, AlertCircle, Eye, EyeOff, UserPlus, LogIn } from 'lucide-react';
import {
  upgradeAnonymousToEmail,
  signInPatient,
  createPatientAccount,
  sendPatientPasswordReset,
  getCurrentPatientAuth,
} from '../../services/auth/authUpgradeService';
import { trackFunnelEvent } from '../../services/analytics/funnelTracker';

type AuthMode = 'login' | 'register';

interface PatientAuthFormProps {
  /** Initial mode — login or register */
  initialMode?: AuthMode;
  /** Called on successful auth with uid and email */
  onSuccess?: (uid: string, email: string) => void;
  /** Called when user wants to dismiss/skip */
  onSkip?: () => void;
  /** Show as compact inline form (no full page chrome) */
  compact?: boolean;
  /** Custom heading for register mode */
  registerHeading?: string;
  /** Custom subtext for register mode */
  registerSubtext?: string;
}

export function PatientAuthForm({
  initialMode = 'login',
  onSuccess,
  onSkip,
  compact = false,
  registerHeading,
  registerSubtext,
}: PatientAuthFormProps) {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    const trimmedEmail = email.trim();
    const trimmedPassword = password;

    if (!trimmedEmail) {
      setError('Email is required.');
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!trimmedPassword) {
      setError('Password is required.');
      return;
    }
    if (trimmedPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (mode === 'register' && trimmedPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      if (mode === 'register') {
        // Try to upgrade anonymous first, fall back to create
        const currentAuth = getCurrentPatientAuth();
        let result;

        if (currentAuth.uid && !currentAuth.isAuthenticated) {
          // Currently anonymous — upgrade
          result = await upgradeAnonymousToEmail(trimmedEmail, trimmedPassword);
        } else {
          // No anonymous session or already authed — create fresh
          result = await createPatientAccount(trimmedEmail, trimmedPassword);
        }

        if (result.success) {
          setSuccessMessage('Account created successfully.');
          trackFunnelEvent({ event: 'ACCOUNT_CREATED' });
          onSuccess?.(result.uid, trimmedEmail);
        } else {
          setError(result.error || 'Account creation failed.');
        }
      } else {
        // Login mode
        const result = await signInPatient(trimmedEmail, trimmedPassword);
        if (result.success) {
          onSuccess?.(result.uid, trimmedEmail);
        } else {
          setError(result.error || 'Sign in failed.');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    setError('');
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setError('Enter your email address above, then click "Forgot password" again.');
      return;
    }

    setLoading(true);
    const result = await sendPatientPasswordReset(trimmedEmail);
    setLoading(false);

    if (result.success) {
      setResetSent(true);
      setSuccessMessage('If an account exists with that email, a reset link has been sent.');
    } else {
      setError(result.error || 'Failed to send reset email.');
    }
  }

  function switchMode() {
    setMode((m) => (m === 'login' ? 'register' : 'login'));
    setError('');
    setSuccessMessage('');
    setResetSent(false);
    setConfirmPassword('');
  }

  // Headings
  const heading = mode === 'register'
    ? (registerHeading || 'Create your account')
    : 'Welcome back';

  const subtext = mode === 'register'
    ? (registerSubtext || 'Save your results and access them anytime.')
    : 'Sign in to view your results and protocols.';

  const containerClass = compact
    ? ''
    : 'min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4';

  return (
    <div className={containerClass} style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className={compact ? 'w-full' : 'w-full max-w-md'}>
        {/* Header */}
        <div className={compact ? 'mb-5' : 'text-center mb-6'}>
          {!compact && (
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#E11D48]/10 mb-3">
              {mode === 'register' ? (
                <UserPlus size={24} className="text-[#E11D48]" />
              ) : (
                <LogIn size={24} className="text-[#E11D48]" />
              )}
            </div>
          )}
          <h2
            className={`text-[#18181B] ${compact ? 'text-lg' : 'text-2xl'} mb-1`}
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            {heading}
          </h2>
          <p className="text-[14px] text-[#52525B] leading-relaxed">
            {subtext}
          </p>
        </div>

        {/* Form card */}
        <div className={compact ? '' : 'bg-white rounded-[4px] border border-[#E4E4E7] p-6 shadow-sm'}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label
                htmlFor="patient-email"
                className="block text-[13px] font-medium text-[#52525B] mb-1.5"
              >
                Email
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
                <input
                  id="patient-email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-3 py-2.5 rounded-[4px] bg-[#F8FAFC] border border-[#E4E4E7] text-[15px] text-[#18181B] placeholder:text-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#E11D48]/20 focus:border-[#E11D48]/50 transition-colors"
                  autoComplete="email"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="patient-password"
                className="block text-[13px] font-medium text-[#52525B] mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
                <input
                  id="patient-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder={mode === 'register' ? 'At least 6 characters' : 'Enter password'}
                  className="w-full pl-10 pr-10 py-2.5 rounded-[4px] bg-[#F8FAFC] border border-[#E4E4E7] text-[15px] text-[#18181B] placeholder:text-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#E11D48]/20 focus:border-[#E11D48]/50 transition-colors"
                  autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#52525B] transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm password (register only) */}
            {mode === 'register' && (
              <div>
                <label
                  htmlFor="patient-confirm-password"
                  className="block text-[13px] font-medium text-[#52525B] mb-1.5"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
                  <input
                    id="patient-confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder="Confirm your password"
                    className="w-full pl-10 pr-3 py-2.5 rounded-[4px] bg-[#F8FAFC] border border-[#E4E4E7] text-[15px] text-[#18181B] placeholder:text-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#E11D48]/20 focus:border-[#E11D48]/50 transition-colors"
                    autoComplete="new-password"
                    disabled={loading}
                    required
                  />
                </div>
              </div>
            )}

            {/* Forgot password (login only) */}
            {mode === 'login' && !resetSent && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={loading}
                  className="text-[13px] text-[#E11D48] hover:text-[#BE123C] transition-colors disabled:opacity-50"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="flex items-start gap-2 text-[#DC2626] text-[13px] bg-[#FEF2F2] px-3 py-2 rounded-[4px] border border-[#FEE2E2]">
                <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Success message */}
            {successMessage && (
              <div className="text-[13px] text-[#059669] bg-[#ECFDF5] px-3 py-2 rounded-[4px] border border-[#D1FAE5]">
                {successMessage}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full py-2.5 rounded-[4px] bg-[#E11D48] text-white font-medium text-[15px] hover:bg-[#BE123C] transition-colors focus:outline-none focus:ring-2 focus:ring-[#E11D48]/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={17} className="animate-spin" />
                  {mode === 'register' ? 'Creating account...' : 'Signing in...'}
                </>
              ) : (
                <>
                  {mode === 'register' ? <UserPlus size={17} /> : <LogIn size={17} />}
                  {mode === 'register' ? 'Create Account' : 'Sign In'}
                </>
              )}
            </button>
          </form>

          {/* Mode switch */}
          <div className="mt-4 pt-4 border-t border-[#F4F4F5] text-center">
            <p className="text-[13px] text-[#A1A1AA]">
              {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}
              {' '}
              <button
                onClick={switchMode}
                className="text-[#E11D48] font-medium hover:text-[#BE123C] transition-colors"
              >
                {mode === 'login' ? 'Create one' : 'Sign in'}
              </button>
            </p>
          </div>

          {/* Skip button */}
          {onSkip && (
            <div className="mt-3 text-center">
              <button
                onClick={onSkip}
                className="text-[13px] text-[#A1A1AA] hover:text-[#52525B] transition-colors"
              >
                Skip for now
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
